import { RealEstateDto } from "api/real-estate"
import { BranchCode, RealEstateEligibilityDtoStatusEnum } from "api/risk"
import GenericTable from "components/display/Table/Table"
import { Column } from "components/display/Table/types"
import { Checkbox } from "components/forms/fragments/Checkbox"
import { PenIcon } from "components/icons/Icons"
import { Button } from "components/ui/Button"
import { AlertCircle, CheckCircle2, Plus, Search, ShieldCheck, Trash2 } from "lucide-react"
import { FC, useEffect, useMemo, useState } from "react"

import { ParcDto } from "api/real-estate"
import { useParcEligibility } from "../../hooks/useParcEligibility"
import { formatAddress, formatEuroPrice } from "../../utils/realEstateFormatters"
import { toDayInput } from 'core/utils/dates'

export interface ParcEligibilitySelection {
  isValid: boolean
  includedRealEstateIds: string[]
  /** GLI : date d'effet (yyyy-mm-dd), undefined sinon. */
  effectiveDate?: string
}

interface ParcEligibilityPanelProps {
  parc: ParcDto
  branchCode: BranchCode
  onChange: (selection: ParcEligibilitySelection) => void
  onEditBien: (bien: RealEstateDto, parcId?: string) => void
  onAddBien?: () => void
  onDeleteParc?: () => void
  refreshKey?: number
}

type Status = RealEstateEligibilityDtoStatusEnum

const STATUS_META: Record<Status, { label: string; pill: string; dot: string; row: string }> = {
  [RealEstateEligibilityDtoStatusEnum.Eligible]: {
    label: "Éligible",
    pill: "bg-green-50 text-green-700 border-green-200",
    dot: "bg-green-500",
    row: ""
  },
  [RealEstateEligibilityDtoStatusEnum.ACompleter]: {
    label: "À compléter",
    pill: "bg-amber-50 text-amber-700 border-amber-200",
    dot: "bg-amber-500",
    row: "bg-amber-50/40"
  },
  [RealEstateEligibilityDtoStatusEnum.NonEligible]: {
    label: "Non éligible",
    pill: "bg-red-50 text-red-700 border-red-200",
    dot: "bg-red-500",
    row: "bg-red-50/50"
  }
}

const PAGE_SIZE = 20
const PAGE_SIZE_OPTIONS = [10, 20, 50]

export const ParcEligibilityPanel: FC<ParcEligibilityPanelProps> = ({
  parc,
  branchCode,
  onChange,
  onEditBien,
  onAddBien,
  onDeleteParc,
  refreshKey
}) => {
  const isGli = branchCode === BranchCode.RGI

  const biens = useMemo(() => parc.realEstates ?? [], [parc.realEstates])
  const bienIds = useMemo(() => biens.map((b) => b.id), [biens])

  // GLI : entrées niveau parc
  const [hasClause, setHasClause] = useState(true)
  const [contractStartDate, setContractStartDate] = useState<string>("")

  // GLI : surcharge de la clause par bail (exceptions au défaut global)
  const [clauseByBien, setClauseByBien] = useState<Record<string, boolean>>({})

  // Exclusions manuelles (biens éligibles que l'on choisit de ne pas assurer)
  const [excludedIds, setExcludedIds] = useState<Set<string>>(new Set())

  const [search, setSearch] = useState("")

  // Quand le défaut global change, on purge les surcharges devenues identiques au global.
  useEffect(() => {
    setClauseByBien((prev) => {
      const next: Record<string, boolean> = {}
      for (const [id, value] of Object.entries(prev)) {
        if (value !== hasClause) next[id] = value
      }
      return next
    })
  }, [hasClause])

  const effectiveClause = (id: string) => (id in clauseByBien ? clauseByBien[id] : hasClause)

  const setBienClause = (id: string, value: boolean) => {
    setClauseByBien((prev) => {
      const next = { ...prev }
      if (value === hasClause) delete next[id]
      else next[id] = value
      return next
    })
  }

  const { verdicts, isLoading } = useParcEligibility(branchCode, bienIds, {
    terminationClauseGlobal: isGli ? hasClause : undefined,
    terminationClauseOverrides: isGli ? clauseByBien : undefined,
    refreshKey
  })

  const today = useMemo(() => toDayInput(new Date()), [])
  const maxDate = useMemo(() => {
    const date = new Date()
    date.setMonth(date.getMonth() + 6)
    return toDayInput(date)
  }, [])

  const dateError = useMemo(() => {
    if (!isGli || !contractStartDate) return undefined
    if (contractStartDate < today) return "La date ne peut pas être antérieure à aujourd'hui."
    if (contractStartDate > maxDate) return "La date ne peut pas dépasser 6 mois à partir d'aujourd'hui."
    return undefined
  }, [isGli, contractStartDate, today, maxDate])

  const isDateValid = !isGli || (contractStartDate.length === 10 && !dateError)

  const isBlocked = (id: string) => verdicts[id]?.status === RealEstateEligibilityDtoStatusEnum.NonEligible
  const isIncluded = (id: string) => !isBlocked(id) && !excludedIds.has(id)

  const toggleInclude = (id: string) => {
    setExcludedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const includedBiens = biens.filter((b) => isIncluded(b.id))
  const nonEligibleCount = biens.filter((b) => isBlocked(b.id)).length
  const aCompleterIncludedCount = includedBiens.filter(
    (b) => verdicts[b.id]?.status === RealEstateEligibilityDtoStatusEnum.ACompleter
  ).length

  // Valide si au moins un bien inclus, tous les inclus sont éligibles, et (GLI) date valide.
  const isValid =
    includedBiens.length > 0 &&
    includedBiens.every((b) => verdicts[b.id]?.status === RealEstateEligibilityDtoStatusEnum.Eligible) &&
    isDateValid

  const includedIdsKey = includedBiens.map((b) => b.id).join(",")

  useEffect(() => {
    onChange({
      isValid,
      includedRealEstateIds: includedIdsKey ? includedIdsKey.split(",") : [],
      effectiveDate: isGli ? contractStartDate || undefined : undefined
    })
  }, [isValid, includedIdsKey, isGli, contractStartDate, onChange])

  const filteredBiens = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return biens
    return biens.filter(
      (b) => b.name?.toLowerCase().includes(term) || formatAddress(b.address).toLowerCase().includes(term)
    )
  }, [biens, search])

  const columns = useMemo<Column<RealEstateDto>[]>(() => {
    const cols: Column<RealEstateDto>[] = [
      {
        key: "id",
        label: "Inclure",
        align: "center",
        width: "72px",
        className: "!px-3",
        render: (_value, row) => (
          <div className="flex justify-center">
            <Checkbox
              checked={isIncluded(row.id)}
              disabled={isBlocked(row.id)}
              onChange={() => toggleInclude(row.id)}
            />
          </div>
        )
      },
      {
        key: "name",
        label: "Bien",
        sortable: true,
        className: "!whitespace-normal align-top !px-3",
        render: (_value, row) => (
          <div className="flex items-start gap-3">
            <img
              src={row.photoUrl || "/assets/images/real_estate_default.png"}
              alt={row.name}
              className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
              loading="lazy"
            />
            <div className="flex flex-col min-w-0">
              <span className="font-medium text-gray-900 truncate">{row.name}</span>
              <span className="text-xs text-gray-500 truncate">{formatAddress(row.address)}</span>
            </div>
          </div>
        )
      },
      {
        key: "propertyType",
        label: "Statut",
        width: "300px",
        className: "!whitespace-normal align-top !px-3",
        render: (_value, row) => {
          const status = verdicts[row.id]?.status
          const meta = status ? STATUS_META[status] : undefined
          const reasons = verdicts[row.id]?.reasons ?? []
          return (
            <div className="flex flex-col gap-1">
              {meta ? (
                <span
                  className={`inline-flex w-fit items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${meta.pill}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                  {meta.label}
                </span>
              ) : (
                <span className="text-xs text-gray-400">—</span>
              )}
              {reasons.length > 0 && (
                <ul className="space-y-0.5">
                  {reasons.map((reason, index) => (
                    <li key={index} className="text-xs text-red-600 break-words">• {reason}</li>
                  ))}
                </ul>
              )}
            </div>
          )
        }
      }
    ]

    if (isGli) {
      cols.push({
        key: "roomCount",
        label: "Clause",
        align: "center",
        width: "120px",
        className: "!px-3",
        render: (_value, row) => (
          <div className="flex flex-col items-center gap-1">
            <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setBienClause(row.id, true)}
                className={`px-2 py-0.5 text-xs font-medium ${effectiveClause(row.id) ? "bg-primary text-white" : "bg-white text-slate-600"}`}
              >
                Oui
              </button>
              <button
                type="button"
                onClick={() => setBienClause(row.id, false)}
                className={`px-2 py-0.5 text-xs font-medium ${!effectiveClause(row.id) ? "bg-primary text-white" : "bg-white text-slate-600"}`}
              >
                Non
              </button>
            </div>
            {row.id in clauseByBien && <span className="text-[11px] text-slate-400">modifié</span>}
          </div>
        )
      })
    }

    cols.push({
      key: "rentAmount",
      label: "Loyer",
      align: "right",
      width: "110px",
      className: "!px-3",
      render: (_value, row) =>
        row.rentAmount && row.rentAmount > 0 ? (
          <span className="font-medium text-gray-900">{formatEuroPrice(row.rentAmount)}</span>
        ) : (
          <span className="text-gray-400 italic">—</span>
        )
    })

    return cols
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGli, verdicts, clauseByBien, hasClause, excludedIds])

  return (
    <div className="rounded-xl border border-primary/15 bg-primary/[0.04] p-4 space-y-4">
      {/* En-tête */}
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-primary" />
        <h3 className="text-base font-semibold text-slate-900">Éligibilité du parc</h3>
        <span className="text-sm text-slate-500">— {biens.length} {biens.length > 1 ? "biens" : "bien"}</span>
        <div className="ml-auto flex items-center gap-2">
          {onAddBien && (
            <Button color="secondary" round onClick={onAddBien}>
              <Plus size={16} />
              Ajouter un bien
            </Button>
          )}
          {onDeleteParc && (
            <Button color="slate" round onClick={onDeleteParc}>
              <Trash2 size={16} />
              Supprimer le parc
            </Button>
          )}
        </div>
      </div>

      {/* GLI : paramètres niveau parc */}
      {isGli && (
        <div className="rounded-lg bg-white border border-gray-100 p-3 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <label className="text-sm font-medium text-slate-700">
              Les baux contiennent-ils une clause résolutoire ?
            </label>
            <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setHasClause(true)}
                className={`px-3 py-1.5 text-sm font-medium ${hasClause ? "bg-primary text-white" : "bg-white text-slate-600"}`}
              >
                Oui pour tous
              </button>
              <button
                type="button"
                onClick={() => setHasClause(false)}
                className={`px-3 py-1.5 text-sm font-medium ${!hasClause ? "bg-primary text-white" : "bg-white text-slate-600"}`}
              >
                Non
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between gap-3">
            <label className="text-sm font-medium text-slate-700">Date d'effet du contrat GLI</label>
            <input
              type="date"
              value={contractStartDate}
              min={today}
              max={maxDate}
              onChange={(e) => setContractStartDate(e.target.value)}
              className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
          </div>
          {dateError && <p className="text-sm text-red-600">{dateError}</p>}
        </div>
      )}

      {/* Recherche (externe au tableau — filtre nom + adresse) */}
      <div className="relative w-full sm:max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un bien, une adresse…"
          className="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
        />
      </div>

      {/* Tableau des biens (paginé — scale à des centaines de biens) */}
      <GenericTable<RealEstateDto>
        config={{
          variant: "soft",
          loading: isLoading,
          skeletonRowCount: 5,
          data: filteredBiens,
          columns,
          getRowClassName: (row) => STATUS_META[verdicts[row.id]?.status as Status]?.row ?? "",
          actions: {
            row: [
              {
                label: "edit",
                render: (row: RealEstateDto, index: number) => (
                  <Button key={index} round color="slate" onClick={() => onEditBien(row, parc.id)}>
                    <PenIcon />
                  </Button>
                )
              }
            ]
          },
          pagination: {
            enabled: true,
            pageSize: PAGE_SIZE,
            totalItems: filteredBiens.length,
            totalPages: Math.max(1, Math.ceil(filteredBiens.length / PAGE_SIZE)),
            showInfo: true,
            pageSizeOptions: PAGE_SIZE_OPTIONS
          }
        }}
      />

      {/* Synthèse */}
      {!isLoading && (
        <div className="flex items-start gap-2">
          {isValid ? (
            <div className="flex items-center gap-2 text-sm text-green-700">
              <CheckCircle2 className="w-4 h-4" />
              {includedBiens.length} {includedBiens.length > 1 ? "biens éligibles inclus" : "bien éligible inclus"}
            </div>
          ) : (
            <div className="flex items-start gap-2 text-sm text-amber-700">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                {includedBiens.length === 0
                  ? "Aucun bien inclus dans le parc."
                  : aCompleterIncludedCount > 0
                    ? `${aCompleterIncludedCount} bien(s) à compléter ou à exclure avant de continuer.`
                    : !isDateValid
                      ? "Renseignez une date d'effet valide pour continuer."
                      : "Des biens inclus ne sont pas éligibles."}
                {nonEligibleCount > 0 && ` ${nonEligibleCount} bien(s) non éligible(s) exclu(s) automatiquement.`}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
